import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { imageAPI } from '../lib/api';
import QuotaErrorBanner from '../components/QuotaErrorBanner';
import {
  Image as ImageIcon,
  AlertTriangle,
  CheckCircle,
  Loader,
  X,
  AlertCircle,
  Settings,
  ArrowRight,
} from 'lucide-react';

const ImageAnalysis = () => {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [error, setError] = useState('');

  const onDrop = useCallback((acceptedFiles) => {
    if (files.length + acceptedFiles.length > 5) {
      setError('Maximum 5 images allowed');
      return;
    }
    setFiles((prev) => [...prev, ...acceptedFiles].slice(0, 5));
    setError('');
  }, [files]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'image/*': ['.png', '.jpg', '.jpeg', '.webp'] },
    maxFiles: 5,
    maxSize: 10 * 1024 * 1024, // 10MB per image
  });

  const removeFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleAnalyze = async () => {
    if (files.length === 0) return;

    setUploading(true);
    setError('');

    try {
      const uploadRes = await imageAPI.upload(files);
      const { analysis_id } = uploadRes.data;

      setUploading(false);
      setAnalyzing(true);

      // Get analysis results
      setTimeout(async () => {
        try {
          const analysisRes = await imageAPI.analyze(analysis_id);
          setAnalysis(analysisRes.data);
          setAnalyzing(false);
        } catch (err) {
          setError('Failed to get analysis results');
          setAnalyzing(false);
        }
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Upload failed');
      setUploading(false);
      setAnalyzing(false);
    }
  };

  const handleReset = () => {
    setFiles([]);
    setAnalysis(null);
    setError('');
  };

  const failureTypeLabels = {
    stringing: 'Stringing',
    under_extrusion: 'Under Extrusion',
    over_extrusion: 'Over Extrusion',
    layer_shift: 'Layer Shift',
    warping: 'Warping',
    adhesion_issues: 'Adhesion Issues',
    nozzle_clog: 'Nozzle Clog',
    temperature_issues: 'Temperature Issues',
    other: 'Other',
  };

  return (
    <div className="max-w-6xl" data-testid="image-analysis-page">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white mb-2">Image Failure Analysis</h1>
        <p className="text-text-secondary">
          Upload photos of your failed print for AI-powered diagnosis and troubleshooting guidance
        </p>
      </div>

      {!analysis ? (
        <div className="space-y-6">
          {/* Upload Area */}
          <div
            {...getRootProps()}
            className={`
              border-2 border-dashed rounded-xl p-12 text-center cursor-pointer transition
              ${isDragActive
                ? 'border-primary bg-primary/10'
                : files.length > 0
                ? 'border-accent bg-accent/10'
                : 'border-text-muted/20 bg-bg-light hover:border-primary/50'
              }
            `}
            data-testid="image-dropzone"
          >
            <input {...getInputProps()} />
            <div className="flex flex-col items-center">
              <ImageIcon className="w-16 h-16 text-text-muted mb-4" />
              <p className="text-xl font-semibold text-white mb-2">
                {isDragActive ? 'Drop your images here' : 'Drag & drop failure images'}
              </p>
              <p className="text-text-secondary mb-4">or click to browse</p>
              <p className="text-sm text-text-muted">Maximum 5 images, 10MB each</p>
            </div>
          </div>

          {/* Preview Images */}
          {files.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {files.map((file, index) => (
                <div key={index} className="relative group">
                  <img
                    src={URL.createObjectURL(file)}
                    alt={`Preview ${index + 1}`}
                    className="w-full h-32 object-cover rounded-lg border border-text-muted/20"
                  />
                  <button
                    onClick={() => removeFile(index)}
                    className="absolute top-2 right-2 p-1 bg-error rounded-full opacity-0 group-hover:opacity-100 transition"
                  >
                    <X className="w-4 h-4 text-white" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {error && (
            <QuotaErrorBanner error={error} onClose={() => setError('')} source="image-analysis" />
          )}

          {files.length > 0 && (
            <button
              onClick={handleAnalyze}
              disabled={uploading || analyzing}
              className="w-full py-4 bg-primary hover:bg-primary-dark text-white rounded-lg font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
              data-testid="analyze-images-btn"
            >
              {uploading || analyzing ? (
                <>
                  <Loader className="w-5 h-5 animate-spin" />
                  <span>{uploading ? 'Uploading...' : 'Analyzing...'}</span>
                </>
              ) : (
                <>
                  <ImageIcon className="w-5 h-5" />
                  <span>Analyze Images ({files.length})</span>
                </>
              )}
            </button>
          )}

          {/* Info Card */}
          <div className="bg-bg-light p-6 rounded-xl border border-text-muted/20">
            <h3 className="font-semibold text-white mb-3">What we can detect:</h3>
            <div className="grid md:grid-cols-2 gap-3">
              {Object.values(failureTypeLabels).map((label) => (
                <div key={label} className="flex items-center space-x-2 text-text-secondary text-sm">
                  <CheckCircle className="w-4 h-4 text-accent" />
                  <span>{label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : (
        /* Analysis Results */
        <div className="space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <CheckCircle className="w-8 h-8 text-success" />
              <div>
                <h2 className="text-2xl font-bold text-white">Diagnosis Complete</h2>
                <p className="text-text-secondary">{files.length} images analyzed</p>
              </div>
            </div>
            <button
              onClick={handleReset}
              className="px-6 py-2 bg-bg-light hover:bg-[#252936] text-white rounded-lg transition"
            >
              Analyze More
            </button>
          </div>

          {/* Detected Failures */}
          {analysis.detected_failures && analysis.detected_failures.length > 0 && (
            <div className="bg-gradient-to-br from-error/20 to-warning/20 p-6 rounded-xl border border-error/20">
              <h3 className="text-lg font-bold text-white mb-4 flex items-center space-x-2">
                <AlertCircle className="w-5 h-5 text-error" />
                <span>Detected Failures</span>
              </h3>
              <div className="flex flex-wrap gap-2">
                {analysis.detected_failures.map((failure, index) => (
                  <span
                    key={index}
                    className="px-4 py-2 bg-bg-dark rounded-lg text-white font-medium"
                  >
                    {failureTypeLabels[failure] || failure}
                  </span>
                ))}
              </div>
              {analysis.ai_diagnosis?.confidence && (
                <div className="mt-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-text-secondary">Confidence</span>
                    <span className="text-sm font-semibold text-white">
                      {Math.round(analysis.ai_diagnosis.confidence * 100)}%
                    </span>
                  </div>
                  <div className="w-full bg-bg-dark rounded-full h-2">
                    <div
                      className="bg-gradient-to-r from-error to-warning h-2 rounded-full transition-all"
                      style={{ width: `${analysis.ai_diagnosis.confidence * 100}%` }}
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Explanation */}
          {analysis.ai_diagnosis?.explanation && (
            <div className="bg-bg-light p-6 rounded-xl border border-text-muted/20">
              <h3 className="text-lg font-bold text-white mb-3">What Went Wrong</h3>
              <p className="text-text-secondary leading-relaxed">
                {analysis.ai_diagnosis.explanation}
              </p>
            </div>
          )}

          {/* Likely Causes */}
          {analysis.ai_diagnosis?.likely_causes && analysis.ai_diagnosis.likely_causes.length > 0 && (
            <div className="bg-bg-light p-6 rounded-xl border border-warning/20">
              <h3 className="text-lg font-bold text-white mb-4 flex items-center space-x-2">
                <AlertTriangle className="w-5 h-5 text-warning" />
                <span>Likely Causes</span>
              </h3>
              <ul className="space-y-2">
                {analysis.ai_diagnosis.likely_causes.map((cause, index) => (
                  <li key={index} className="flex items-start space-x-2 text-text-secondary">
                    <span className="text-warning mt-1">•</span>
                    <span>{cause}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Recommended Fixes */}
          {analysis.ai_diagnosis?.recommended_fixes && analysis.ai_diagnosis.recommended_fixes.length > 0 && (
            <div className="bg-bg-light p-6 rounded-xl border border-accent/20">
              <h3 className="text-lg font-bold text-text-primary mb-4 flex items-center space-x-2">
                <CheckCircle className="w-5 h-5 text-accent" />
                <span>How to Fix It</span>
              </h3>
              <ol className="space-y-3">
                {analysis.ai_diagnosis.recommended_fixes.map((fix, index) => (
                  <li key={index} className="flex items-start space-x-3 text-text-secondary">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-accent/20 text-accent flex items-center justify-center text-sm font-semibold">
                      {index + 1}
                    </span>
                    <span className="flex-1">{fix}</span>
                  </li>
                ))}
              </ol>
            </div>
          )}

          {/* Slicer Settings - AI Fix-it Recommendations */}
          {analysis.ai_diagnosis?.slicer_settings && analysis.ai_diagnosis.slicer_settings.length > 0 && (
            <div className="bg-bg-light p-6 rounded-xl border border-primary/20" data-testid="slicer-settings-section">
              <h3 className="text-lg font-bold text-text-primary mb-4 flex items-center space-x-2">
                <Settings className="w-5 h-5 text-primary" />
                <span>Exact Slicer Settings to Change</span>
              </h3>
              <div className="space-y-3">
                {analysis.ai_diagnosis.slicer_settings.map((s, i) => (
                  <div key={i} className="bg-bg-dark/50 rounded-lg p-4 border border-text-muted/10">
                    <div className="flex items-start justify-between mb-2">
                      <span className="font-semibold text-text-primary text-sm">{s.setting}</span>
                      <span className="text-xs text-text-muted bg-bg-dark px-2 py-0.5 rounded">{s.slicer}</span>
                    </div>
                    <div className="flex items-center gap-3 mb-2">
                      <div className="flex items-center gap-1.5">
                        <span className="text-xs text-text-muted">Current:</span>
                        <span className="text-sm text-red-400 font-mono bg-red-500/10 px-2 py-0.5 rounded">{s.current_likely}</span>
                      </div>
                      <ArrowRight className="w-4 h-4 text-text-muted" />
                      <div className="flex items-center gap-1.5">
                        <span className="text-xs text-text-muted">Change to:</span>
                        <span className="text-sm text-green-400 font-mono bg-green-500/10 px-2 py-0.5 rounded font-semibold">{s.recommended}</span>
                      </div>
                    </div>
                    <p className="text-xs text-text-secondary">{s.reason}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ImageAnalysis;
